// Display formatting for dates (APP-018). Business dates are plain `YYYY-MM-DD` values and never
// shift with any time zone. Instants (ISO timestamps) are shown in the organization time zone, the
// zone that decides "today" and every deadline, so a step taken at 7:49 pm Pacific reads as that
// day for everyone. The audit trail alone shows UTC, to the second (board G1).

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const DEFAULT_ZONE = 'America/Los_Angeles';

let organizationZone = DEFAULT_ZONE;

/** Set once the signed-in person's `me` arrives; an unknown zone keeps the current one. */
export function setOrganizationTimeZone(zone: string): void {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    organizationZone = zone;
  } catch {
    // An invalid zone never breaks the page; dates stay in the previous zone.
  }
}

const formats = new Map<string, Intl.DateTimeFormat>();

/** One formatter per shape and zone, built on first use. */
function formatter(name: string, zone: string, options: Intl.DateTimeFormatOptions) {
  const key = `${name}|${zone}`;
  let format = formats.get(key);
  if (format === undefined) {
    format = new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone });
    formats.set(key, format);
  }
  return format;
}

/** A business date at UTC midnight with the UTC zone, or an instant with the organization's. */
function dated(value: string): { date: Date; zone: string } {
  const match = DATE_ONLY.exec(value);
  if (match) {
    return {
      date: new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))),
      zone: 'UTC',
    };
  }
  return { date: new Date(value), zone: organizationZone };
}

const DATE: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

/** "Oct 2, 2026" from `2026-10-02`, or the organization's day of an ISO timestamp. */
export function formatDate(value: string): string {
  const { date, zone } = dated(value);
  return formatter('date', zone, DATE).format(date);
}

/** "Oct 18, 2026, 7:49 PM PDT" from an ISO timestamp, in the organization time zone. */
export function formatTimestamp(value: string): string {
  return formatter('timestamp', organizationZone, {
    ...DATE,
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

/** "9:20 AM PDT" from an ISO timestamp: the time of day in the organization time zone. */
export function formatTimeOfDay(value: string): string {
  return formatter('time', organizationZone, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
}

/**
 * A recent instant that scans at a glance (QA-040): "Today, 2:00 AM PDT", "Yesterday, 2:00 AM PDT",
 * "Oct 17, 2:00 AM PDT" this year, else the whole timestamp. Days are the organization's.
 */
export function formatRecentTime(value: string, now: Date = new Date()): string {
  const time = formatter('time', organizationZone, {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(value));
  const day = organizationDate(value);
  const today = organizationDate(now.toISOString());
  const gap = daysBetween(day, today);
  if (gap === 0) return `Today, ${time}`;
  if (gap === 1) return `Yesterday, ${time}`;
  if (gap > 1 && day.slice(0, 4) === today.slice(0, 4)) return `${formatShortDate(value)}, ${time}`;
  return formatTimestamp(value);
}

/** "Oct 15, 2026 · 15:46:11 UTC": the audit trail's time, to the second (board G1). */
export function formatAuditTimestamp(value: string): string {
  const date = new Date(value);
  const day = formatter('date', 'UTC', DATE).format(date);
  const time = formatter('audit-time', 'UTC', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(date);
  return `${day} · ${time} UTC`;
}

/** "Oct 21" from `2026-10-21`, or the organization's day of an ISO timestamp. */
export function formatShortDate(value: string): string {
  const { date, zone } = dated(value);
  return formatter('short', zone, { month: 'short', day: 'numeric' }).format(date);
}

/** "Wednesday, Oct 14, 2026" from `2026-10-14`, or the organization's day of an ISO timestamp. */
export function formatWeekdayDate(value: string): string {
  const { date, zone } = dated(value);
  return formatter('weekday', zone, { weekday: 'long', ...DATE }).format(date);
}

/** "Oct" from `2026-10` (a month) or `2026-10-14`. */
export function formatMonth(value: string): string {
  const { date, zone } = dated(value.length === 7 ? `${value}-01` : value);
  return formatter('month', zone, { month: 'short' }).format(date);
}

/** The organization's hour (0–23) of an instant: "this morning" or "this afternoon". */
export function organizationHour(value: string): number {
  const hour = formatter('hour', organizationZone, { hour: 'numeric', hourCycle: 'h23' })
    .formatToParts(new Date(value))
    .find((p) => p.type === 'hour')?.value;
  return Number(hour ?? 0) % 24;
}

/**
 * The organization's day of an ISO timestamp as `YYYY-MM-DD` (a business date passes through), for
 * counting days from an instant ("14 days since the letter").
 */
export function organizationDate(value: string): string {
  if (DATE_ONLY.test(value)) return value;
  const parts = formatter('iso-day', organizationZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(value));
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Whole days from one business date to another (`2026-10-14` → `2026-10-21` is 7). */
export function daysBetween(from: string, to: string): number {
  return Math.round((dated(to).date.getTime() - dated(from).date.getTime()) / 86_400_000);
}

const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const withCents = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

/** "$155,905" from 15590500 cents; "$12.50" when there are cents; "−$2,913" when negative. */
export function formatMoney(cents: number): string {
  const formatter = cents % 100 === 0 ? wholeDollars : withCents;
  const text = formatter.format(Math.abs(cents) / 100);
  return cents < 0 ? `−${text}` : text;
}

const countFormat = new Intl.NumberFormat('en-US');

/** "7,956"; "−12" when negative. */
export function formatCount(value: number): string {
  const text = countFormat.format(Math.abs(value));
  return value < 0 ? `−${text}` : text;
}

/** "1 person", "3 people"; "1 award", "4 awards". */
export function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}
